import DesktopEditProfile from 'components/Profile/EditProfile/DesktopEditProfile';
import EditProfileImageDialog from 'components/Profile/EditProfile/EditProfileImageDialog';
import MobileEditProfile from 'components/Profile/EditProfile/MobileEditProfile';
import TabletEditProfile from 'components/Profile/EditProfile/TabletEditProfile';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormValidation from 'services/hooks/useFormValidation';

const EditProfile = ({ setSelected, setAlertDialog, setOnBeforeChange }) => {
    const { auth, currentUser, setCurrentUser, updateProfile } = useAuth();
    const { setAccountUsers } = useApp();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation();

    const defaultFormValues = {
        ...currentUser,
        country: currentUser.country || null,
        timezone: currentUser.timezone || null,
    };

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(defaultFormValues);
    const [isDirty, setIsDirty] = useState(false);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [dialog, setDialog] = useState({ open: false });

    const { errors, validate, validateAll } = useFormValidation(form, (key, value) => {
        if (['first_name', 'last_name'].includes(key)) value = value?.trim();
        switch (key) {
            case 'first_name':
                return value ? '' : t('account-settings.profile.enter-first-name', 'Please enter your first name');
            case 'last_name':
                return value ? '' : t('error.empty-last-name', 'Please enter your last name');
            default:
                return '';
        }
    });

    const handleChange = (key, value) => {
        if (['pronoun', 'english_proficiency', 'country', 'timezone'].includes(key)) {
            setForm((current) => ({ ...current, [key]: value?.key || null }));
        } else {
            setForm((current) => ({ ...current, [key]: value }));
        }
        setIsDirty(true);
        if (hasClickedSubmit || errors[key]) {
            validate(key, value);
        }
    };

    const handleSubmit = async () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;
        setLoading(true);
        const data = {
            profile_image: form.profile_image,
            profile_circle: form.profile_circle,
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            display_name: form.display_name.trim(),
            email: form.email.trim(),
            pronoun: form.pronoun,
            english_proficiency: form.english_proficiency,
            country: form.country,
            timezone: form.timezone,
        };

        await updateProfile(data);
        setCurrentUser((current) => {
            return { ...current, ...data };
        });
        setAccountUsers((current) => {
            if (!current) return current;
            return current.map((user) => {
                if (user.owner_account_id === auth.owner_account_id) {
                    return {
                        ...user,
                        ...data,
                    };
                }
                return user;
            });
        });

        setIsDirty(false);
        setLoading(false);
        message.success(t('message.profile-updated', 'Your profile has been updated.'));
    };

    const handleCancel = () => {
        setForm(defaultFormValues);
        setHasClickedSubmit(false);
        setIsDirty(false);
    };

    useEffect(() => {
        if (!isDirty) validateAll();
    }, [isDirty, validateAll]);

    useEffect(() => {
        if (isDirty) {
            setOnBeforeChange({
                action: (proceed) => {
                    setAlertDialog((current) => ({
                        ...current,
                        open: true,
                        message: t('dialog.leave-page.title', 'Leave Page?'),
                        description: t(
                            'dialog.leave-page.description',
                            'If you leave this page, your changes will be lost.'
                        ),
                        onAction: () => {
                            proceed();
                            setAlertDialog((current) => ({ ...current, open: false }));
                        },
                    }));
                },
            });
        } else {
            setOnBeforeChange({ action: (proceed) => proceed() });
        }
    }, [isDirty, setAlertDialog, setOnBeforeChange, t]);

    const props = {
        loading,
        form,
        isDirty,
        errors,
        onChange: handleChange,
        onSubmit: handleSubmit,
        onCancel: handleCancel,
        onClickEditProfileImage: () => setDialog({ open: true }),
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: (
                <MobileEditProfile
                    {...props}
                    setSelected={(value) => {
                        if (isDirty) {
                            setAlertDialog((current) => ({
                                ...current,
                                open: true,
                                message: t('dialog.leave-page.title', 'Leave Page?'),
                                description: t(
                                    'dialog.leave-page.description',
                                    'If you leave this page, your changes will be lost.'
                                ),
                                onAction: () => {
                                    setSelected(value);
                                    setAlertDialog((current) => ({ ...current, open: false }));
                                },
                            }));
                        } else {
                            setSelected(value);
                        }
                    }}
                />
            ),
            tablet: <TabletEditProfile {...props} />,
            desktop: <DesktopEditProfile {...props} />,
        }[device];
    };

    return (
        <>
            {renderResponsiveLayout(device)}
            <EditProfileImageDialog
                open={dialog.open}
                onClose={() => setDialog({ open: false })}
                form={form}
                onChange={handleChange}
            />
        </>
    );
};

export default EditProfile;
